// Fratelli Ghibellini — motion (GSAP + ScrollTrigger + SplitText, self-hosted).
// Principles: slow, few, meaningful. Nothing on screen at load is ever hidden: initial states are set
// here, not in CSS, and only for things still below the fold, so a background tab (where animation
// frames are paused), a slow script or a failed download can never leave content invisible. The page
// titles above the fold settle into place in CSS from a visible state (fg-theme.css, "Titles settle").
// The whole layer is skipped for prefers-reduced-motion or if GSAP failed to load.

// ---------- Two touches that need no library: the foil and the pen ----------
// Each happens once per page view, the first time its element is seen. CSS does the moving
// (.fg-foil.is-lit, .fg-cup.is-pen.is-drawn in fg-theme.css); this only says when.
(() => {
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches || !('IntersectionObserver' in window)) return;
  const once = (elements, className, threshold) => {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add(className);
        observer.unobserve(entry.target);
      });
    }, { threshold });
    elements.forEach((el) => observer.observe(el));
  };

  // Foil: light crosses the gold lion.
  once(document.querySelectorAll('.fg-foil'), 'is-lit', 0.6);

  // Cup profile: the shape is drawn like a pen stroke. The pen is only lifted for charts still below
  // the fold, so a chart already on screen is never blanked.
  const charts = [...document.querySelectorAll('.fg-cup')].filter((el) => el.getBoundingClientRect().top > window.innerHeight * 0.85);
  charts.forEach((el) => el.classList.add('is-pen'));
  once(charts, 'is-drawn', 0.35);
})();

(() => {
  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const { gsap, ScrollTrigger, SplitText } = window;
  if (reduce || !gsap || !ScrollTrigger) return;
  gsap.registerPlugin(ScrollTrigger);
  if (SplitText) gsap.registerPlugin(SplitText);

  const EASE = 'expo.out';
  const fold = window.innerHeight;
  // Entirely below the first screen: only these may start hidden (a heading or card whose top edge
  // already shows at the bottom of the screen stays as it is).
  const below = (el) => el.getBoundingClientRect().top >= fold;
  const root = document.documentElement;
  root.classList.add('fg-motion');

  // ---------- Headlines below the fold rise line by line from behind a mask ----------
  // Only headings that are still out of sight are split and lowered, so what the visitor (and the LCP)
  // sees first is never waiting on an animation. Split once the display font is ready (never waiting more
  // than 0.7s), then put the plain text back when the animation ends, so resizes and screen readers only
  // ever see the original heading. The page title at the top is left alone here (CSS settles it).
  const fontsReady = Promise.race([document.fonts?.ready || Promise.resolve(), new Promise((r) => setTimeout(r, 700))]);
  const splitRise = (el) => {
    if (!SplitText) return;
    const split = SplitText.create(el, { type: 'lines', mask: 'lines', linesClass: 'fg-line' });
    gsap.from(split.lines, {
      yPercent: 110,
      duration: 1.3,
      ease: EASE,
      stagger: 0.09,
      scrollTrigger: { trigger: el, start: 'top 88%', once: true },
      onComplete: () => split.revert(),
    });
  };

  fontsReady.then(() => {
    document
      .querySelectorAll('.fg-h2, .fg-spotlight__title, .fg-product__title, .fg-footer__title')
      .forEach((el) => { if (below(el)) splitRise(el); });
    ScrollTrigger.refresh();
  });

  // ---------- Quiet reveals for cards and blocks ----------
  const REVEAL = [
    '.fg-section-head .fg-eyebrow',
    '.fg-section-head__text',
    '.fg-grid > li',
    '.fg-lines__item',
    '.fg-origin',
    '.fg-producer',
    '.fg-value',
    '.fg-chapter__text',
    '.fg-chapter__media',
    '.fg-heritage__mark',
    '.fg-heritage__crest',
    '.fg-spotlight__facts',
  ].join(',');
  const items = gsap.utils.toArray(REVEAL).filter(below);
  gsap.set(items, { autoAlpha: 0, y: 28 });
  ScrollTrigger.batch(items, {
    start: 'top 90%',
    once: true,
    onEnter: (batch) => gsap.to(batch, { autoAlpha: 1, y: 0, duration: 1.1, ease: EASE, stagger: 0.09, overwrite: true }),
  });

  // ---------- Spotlight: the product settles as you arrive ----------
  document.querySelectorAll('.fg-spotlight__media img, .fg-coll-hero__media img, .fg-page-hero__media img').forEach((img) => {
    gsap.fromTo(img, { scale: 1.08 }, {
      scale: 1,
      ease: 'none',
      scrollTrigger: { trigger: img.parentElement, start: 'top bottom', end: 'bottom top', scrub: true },
    });
  });

  // ---------- The gold thread: farm-to-table steps, pinned on large screens ----------
  const mm = gsap.matchMedia();
  document.querySelectorAll('[data-fg-journey]').forEach((section) => {
    const list = section.querySelector('.fg-journey__list');
    const steps = gsap.utils.toArray(section.querySelectorAll('.fg-journey__step'));
    if (!list || steps.length < 3) return;
    const thread = document.createElement('span');
    thread.className = 'fg-journey__thread';
    thread.setAttribute('aria-hidden', 'true');
    list.prepend(thread);

    mm.add('(min-width: 1200px)', () => {
      section.classList.add('is-pinned');
      const headerH = () => document.querySelector('.fg-header')?.offsetHeight || 0;
      section.style.setProperty('--fg-header-h', `${headerH()}px`);
      const distance = () => Math.max(0, list.scrollWidth - list.clientWidth);
      const tl = gsap.timeline({
        defaults: { ease: 'none' },
        scrollTrigger: {
          trigger: section,
          start: () => `top ${headerH()}`,
          end: () => `+=${distance() + window.innerHeight * 0.35}`,
          pin: true,
          scrub: 0.8,
          invalidateOnRefresh: true,
        },
      });
      tl.to(list, { x: () => -distance() }, 0).fromTo(thread, { scaleX: 0 }, { scaleX: 1 }, 0);
      steps.forEach((step, i) => {
        ScrollTrigger.create({
          trigger: step,
          containerAnimation: tl,
          start: 'left 75%',
          onEnter: () => step.classList.add('is-lit'),
          onLeaveBack: () => step.classList.remove('is-lit'),
        });
        const img = step.querySelector('.fg-journey__media img');
        if (img) gsap.fromTo(img, { scale: 1.12 }, { scale: 1, ease: 'none', scrollTrigger: { trigger: step, containerAnimation: tl, start: 'left right', end: 'right left', scrub: true } });
        if (i === 0) step.classList.add('is-lit');
      });
      return () => {
        section.classList.remove('is-pinned');
        steps.forEach((s) => s.classList.remove('is-lit'));
        gsap.set(list, { clearProps: 'transform' });
      };
    });

    mm.add('(max-width: 1199px)', () => {
      gsap.fromTo(thread, { scaleX: 0 }, { scaleX: 1, duration: 1.8, ease: EASE, scrollTrigger: { trigger: list, start: 'top 80%', once: true } });
      steps.forEach((s) => s.classList.add('is-lit'));
    });
  });

  // ---------- The map draws itself; pins settle; routes run to Lima ----------
  document.querySelectorAll('.fg-map').forEach((map) => {
    const land = map.querySelector('.fg-map__land path');
    const pins = map.querySelectorAll('.fg-map__pin');
    const routes = map.querySelectorAll('.fg-map__route');
    if (!land) return;
    const length = land.getTotalLength();
    gsap.set(land, { strokeDasharray: length, strokeDashoffset: length, fillOpacity: 0 });
    gsap.set(pins, { scale: 0, autoAlpha: 0 });
    routes.forEach((r) => { const l = r.getTotalLength(); gsap.set(r, { strokeDasharray: `${l} ${l}`, strokeDashoffset: l }); });
    gsap.timeline({ scrollTrigger: { trigger: map, start: 'top 80%', once: true } })
      .to(land, { strokeDashoffset: 0, duration: 2.4, ease: 'power2.inOut' })
      .to(land, { fillOpacity: 1, duration: 1, ease: 'power1.out' }, '-=0.8')
      .to(pins, { scale: 1, autoAlpha: 1, duration: 0.8, ease: EASE, stagger: 0.15 }, '-=0.6')
      .to(routes, { strokeDashoffset: 0, duration: 1.4, ease: 'power2.inOut', stagger: 0.12 }, '-=0.3')
      .add(() => routes.forEach((r) => r.classList.add('is-drawn')));
  });

  // ---------- Stamps are pressed on as they arrive ----------
  gsap.utils.toArray('.fg-stamp').filter(below).forEach((stamp) => {
    gsap.from(stamp, {
      scale: 1.35,
      rotate: -18,
      autoAlpha: 0,
      duration: 1.1,
      ease: EASE, // pressed, not bounced: a long ease-out, no overshoot
      scrollTrigger: { trigger: stamp, start: 'top 92%', once: true },
    });
  });

  // Fonts can change line breaks: re-measure once they are ready.
  document.fonts?.ready.then(() => ScrollTrigger.refresh());
})();
